import { email as supportEmail } from '@getpopsure/private-constants';
import { setAccountDataInQuestionnaire } from 'actions/account';
import * as EmailComponents from 'components/AccountEmail/components/Email/Email';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'reducers';
import { getRequestError, getRequestInProcess } from 'selectors/requests';
import { getEmail } from 'selectors/user';
import { emailAddressValidator } from 'shared/util/emailValidator';

import styles from './EmailForm.module.scss';

const { Title, Subtitle, Input, Actions, GoogleError } = EmailComponents;

interface Props {
  email: string;
  loading?: boolean;
  error?: unknown;
  onInputChange(email: string): void;
  onSubmit(): void;
}

const Email = ({ email, loading, error, onInputChange, onSubmit }: Props) => {
  const dispatch = useDispatch();
  const accountEmail = useSelector(getEmail);
  const loadingGoogle = useSelector((state: AppState) =>
    getRequestInProcess(state, 'SET_ACCOUNT_DATA_IN_QUESTIONNAIRE')
  );
  const errorGoogle = useSelector((state: AppState) =>
    getRequestError(state, 'SET_ACCOUNT_DATA_IN_QUESTIONNAIRE')
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoginWithGoogle, setIsLoginWithGoogle] = useState(false);
  const [continueWithEmail, setContinueWithEmail] = useState(false);
  const [googleLoginError, setGoogleLoginError] = useState(false);

  const valid = emailAddressValidator(email) && termsAccepted;

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onInputChange(e.target.value);
  };

  const handleSetTermsAccepted = () => {
    setTermsAccepted((value) => !value);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (valid) {
      onSubmit();
    }
  };

  const handleOnGoogleLogin = () => {
    dispatch(
      setAccountDataInQuestionnaire(
        '/recommendation-tool/quickRecommendation/questionnaire/personal-info/quick-financial-responsibilities',
        'userCreation'
      )
    );
    setIsLoginWithGoogle(true);
  };

  useEffect(() => {
    if (!errorGoogle && !loadingGoogle && accountEmail && isLoginWithGoogle) {
      setIsLoginWithGoogle(false);
      onSubmit();
    }
  }, [errorGoogle, loadingGoogle, accountEmail, onSubmit, isLoginWithGoogle]);

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <div className="p-body">
        <Title />
        <Subtitle />
        <Input
          show={continueWithEmail}
          showTerms
          value={email}
          emailInputOnChange={handleInputChange}
          tAndCInputOnChange={handleSetTermsAccepted}
        />
        <AnimateHeight duration={300} height={error ? 'auto' : 0}>
          <div className="p-p p-notice--warning mt16">
            {`An error occurred while submitting your email address. Please try
            again or contact us at ${supportEmail.help}`}
          </div>
        </AnimateHeight>
        {googleLoginError && <GoogleError />}
        <Actions
          isEmailSet={continueWithEmail}
          loading={loading}
          disabled={!valid}
          setContinueWithEmail={setContinueWithEmail}
          googleOnSuccess={handleOnGoogleLogin}
          googleOnError={() => {
            setIsLoginWithGoogle(false);
            setGoogleLoginError(true);
          }}
        />
      </div>
    </form>
  );
};

export default Email;
